import React, { useState, useEffect } from "react";
import { useAuth, useSignIn, useClerk } from "@clerk/clerk-react";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  IconButton,
  Divider,
  Stack,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { ReactComponent as GoogleIcon } from "../static/Icons/google.svg";
import { ReactComponent as GithubIcon } from "../static/Icons/github.svg";
import { ReactComponent as MicrosoftIcon } from "../static/Icons/microsoft.svg";
import { ReactComponent as MCPlogo } from "../static/Icons/mcp-logo.svg";

const SignIn = () => {
  const { signIn, isLoaded } = useSignIn();
  const { getToken, isSignedIn } = useAuth();
  const { setActive } = useClerk();

  const [form, setForm] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Handle OAuth callback and redirect
  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Check if user is signed in after OAuth redirect
      if (isSignedIn && signIn) {
        try {
          const token = await getToken({ template: "mcp-auth" });
          localStorage.setItem("mcp_token", token);
          window.location.href = "/dashboard";
        } catch (err) {
          console.error("Token error:", err);
        }
      }
    };

    // Check for password reset success message
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('message') === 'password_reset_success') {
      setError(""); // Clear any existing errors
      setForm({ email: "", password: "" }); // Clear form
      // You could also show a success message here if needed
    }

    if (isLoaded) {
      handleOAuthCallback();
    }
  }, [isSignedIn, isLoaded, getToken, signIn]);

  useEffect(() => {
    if (isLoaded && signIn) {
      // Check for errors in the sign-in attempt
      if (
        signIn.status === "needs_identifier" &&
        signIn.firstFactorVerification?.error
      ) {
        const clerkError = signIn.firstFactorVerification.error;

        if (clerkError.code === "external_account_not_found") {
          setError(
            "This Google account is not registered. Please create an account first or use a different Google account."
          );
        } else {
          setError(
            clerkError.long_message ||
              clerkError.message ||
              "Authentication failed"
          );
        }

        setLoading(false);
      }
    }
  }, [signIn, isLoaded]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Simplified OAuth handlers using authenticateWithRedirect
  const handleGoogleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const baseUrl = window.location.origin;
      await signIn.authenticateWithRedirect({
        strategy: "oauth_google",
        redirectUrl: `${baseUrl}/dashboard`,
        redirectUrlComplete: `${baseUrl}/dashboard`,
      });
    } catch (err) {
      console.error("Google sign-in error:", err);
      setError("Google sign-in failed. Please try again.");
      setLoading(false);
    }
  };

  const handleGitHubSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const baseUrl = window.location.origin;
      await signIn.authenticateWithRedirect({
        strategy: "oauth_github",
        redirectUrl: `${baseUrl}/dashboard`,
        redirectUrlComplete: `${baseUrl}/dashboard`,
      });
    } catch (err) {
      console.error("GitHub sign-in error:", err);
      setError("GitHub sign-in failed. Please try again.");
      setLoading(false);
    }
  };

  const handleMicrosoftSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const baseUrl = window.location.origin;
      await signIn.authenticateWithRedirect({
        strategy: "oauth_microsoft",
        redirectUrl: `${baseUrl}/dashboard`,
        redirectUrlComplete: `${baseUrl}/dashboard`,
      });
    } catch (err) {
      console.error("Microsoft sign-in error:", err);
      setError("Microsoft sign-in failed. Please try again.");
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!form.email || !form.password) {
        setError("Please fill in all fields");
        setLoading(false);
        return;
      }

      const signInAttempt = await signIn.create({
        identifier: form.email,
        password: form.password,
      });

      if (signInAttempt.status === "complete") {
        await setActive({ session: signInAttempt.createdSessionId });

        const token = await getToken({ template: "mcp-auth" });
        localStorage.setItem("mcp_token", token);

        window.location.href = "/dashboard";
      } else if (signInAttempt.status === "needs_second_factor") {
        setError("Two-factor authentication is required");
      } else {
        setError("Sign-in failed with status: " + signInAttempt.status);
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err?.errors?.[0]?.message || err.message || "Sign-in failed.");
    } finally {
      setLoading(false);
    }
  };

  // Return loading state if Clerk isn't loaded yet
  if (!isLoaded) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        bgcolor: "#2B2B2B",
        color: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: "500px", p: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 4,
          }}
        >
          <Box
            component={MCPlogo}
            sx={{
              width: 250,
              height: 60,
            }}
          />
        </Box>
        <Box sx={{ border: "1px solid #BCBCBC", borderRadius: 2, p: 2 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontWeight: "bold", textAlign: "center" }}
          >
            Login
          </Typography>

          <>
            {/* Social Media Icons */}
            <Stack
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
              }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  cursor: "pointer",
                  opacity: loading ? 0.6 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
                onClick={handleGoogleSignup}
              >
                <GoogleIcon />
              </Box>

              <Box
                sx={{
                  textAlign: "center",
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  cursor: "pointer",
                  opacity: loading ? 0.6 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
                onClick={handleGitHubSignup}
              >
                <GithubIcon />
              </Box>

              <Box
                sx={{
                  textAlign: "center",
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "center",
                  gap: 1,
                  cursor: "pointer",
                  opacity: loading ? 0.6 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
                onClick={handleMicrosoftSignup}
              >
                <MicrosoftIcon />
              </Box>
            </Stack>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                my: 3,
              }}
            >
              <Divider sx={{ flexGrow: 1, bgcolor: "#fff" }} />
              <Typography
                variant="body2"
                component="span"
                sx={{ px: 2, color: "#fff" }}
              >
                Or
              </Typography>
              <Divider sx={{ flexGrow: 1, bgcolor: "#fff" }} />
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Email Address
              </Typography>
              <TextField
                fullWidth
                name="email"
                type="email"
                placeholder="Enter your email address"
                value={form.email}
                onChange={handleChange}
                required
                variant="outlined"
                sx={{
                  mb: 2.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    color: "#BCBCBC",
                    "& fieldset": {
                      borderColor: "#BCBCBC",
                    },
                    "&:hover fieldset": {
                      borderColor: "#BCBCBC",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#BCBCBC",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#BCBCBC",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#BCBCBC",
                  },
                }}
                size="small"
              />

              <Typography variant="body1" sx={{ mb: 1 }}>
                Password
              </Typography>
              <TextField
                fullWidth
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={form.password}
                onChange={handleChange}
                required
                variant="outlined"
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        sx={{ color: "#BCBCBC" }}
                      >
                        {showPassword ? (
                          <VisibilityIcon />
                        ) : (
                          <VisibilityOffIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    color: "#BCBCBC",
                    "& fieldset": {
                      borderColor: "#BCBCBC",
                    },
                    "&:hover fieldset": {
                      borderColor: "#BCBCBC",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#BCBCBC",
                    },
                  },
                }}
              />

              {error && (
                <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mt: 2, mb: 2, textAlign: "right" }}>
                <Link
                  href="/forgot-password"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: "medium",
                    color: "#CFFF04",
                    fontSize: "0.875rem",
                  }}
                >
                  Forgot Password?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: 9999,
                  bgcolor: "#CFFF04",
                  color: "#000",
                  boxShadow: "0px 4px 10.2px 3px #CFFF045C",
                  textTransform: "capitalize",
                  "&:hover": {
                    bgcolor: "#b0ff12",
                  },
                  fontWeight: 600,
                }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "Login"}
              </Button>
            </Box>
          </>

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2">
              Don't have an account?{" "}
              <Link
                href={"/signup"}
                mode="modal"
                sx={{
                  textDecoration: "none",
                  cursor: "pointer",
                  fontWeight: "medium",
                }}
              >
                SignUp
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SignIn;
